import { useMemo } from "react";
import { useIntl } from "react-intl";
import { graphql, useSubscription } from "react-relay";
import { GraphQLSubscriptionConfig } from "relay-runtime";
import ErrorPage from "./ErrorPage";
import { MetaLayout } from "@/common/MetaLayout";
import CompetitionApprovals from "@/components/CompetitionApprovals";
import CompetitionJury from "@/components/CompetitionJury";
import { useCompetitionApprovalPageQuery } from "./loaders/CompetitionApprovalPage";
import { CompetitionApprovalPageSubscription as CompetitionApprovalPageSubscriptionType } from "./__generated__/CompetitionApprovalPageSubscription.graphql";
import CompetitionScoringCriteria from "@/components/CompetitionScoringCriteria";

const CompetitionApprovalPageSubscription = graphql`
  subscription CompetitionApprovalPageSubscription($competitionId: ID!) {
    projectVersionStatusUpdate(competitionId: $competitionId) {
      project {
        latest {
          ...SubmissionStatusBadgeFragment
          ...BaseProjectVersionApprovalButtonFragment
        }
      }
    }
  }
`;

export default function CompetitionApprovalPage() {
  const intl = useIntl();
  const {
    query: { competitionBySlug: competition },
    variables: { approvalFilter },
  } = useCompetitionApprovalPageQuery();
  useSubscription(
    useMemo(
      (): GraphQLSubscriptionConfig<CompetitionApprovalPageSubscriptionType> => ({
        variables: { competitionId: competition?.id || "" },
        subscription: CompetitionApprovalPageSubscription,
        onError: (error) => {
          if (competition?.id) {
            console.error(error);
          }
        },
      }),
      [competition?.id],
    ),
  );
  if (!competition) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({
          defaultMessage: "Competition not found",
        })}
      />
    );
  }
  return (
    <MetaLayout
      metaTitle={intl.formatMessage(
        { defaultMessage: "{CompetitionTitle} | leaderboard" },
        { CompetitionTitle: competition.title },
      )}
    >
      <div className="pb-8 flex flex-col gap-8">
        <CompetitionJury competition={competition} />
        <CompetitionScoringCriteria competition={competition} />
        <CompetitionApprovals
          competition={competition}
          defaultFilters={{ approvalFilter: approvalFilter || undefined }}
        />
      </div>
    </MetaLayout>
  );
}
